import { Paged } from 'domains/appDomain';
import {
  BUSINESS_TYPE_ENUM,
  GallerySku,
  PlatformSkuLabel,
  PreConfiguredSkuGallery,
  SkuCompanyLabelUpdateParams,
  SkuGalleryLabel,
  SkuGalleryLabelCreateParams,
  SkuGalleryLabelUpdateParams,
  SsoBuyerGalleryLabel,
} from 'domains/gallerySkuDomain';

import api from 'utils/api';
import {
  GalleryDesignSize,
  galleryDesignSizeQuery,
} from 'utils/galleryDesignSize';
import {
  GallerySkuFilter,
  gallerySkuFilterQuery,
} from 'utils/gallerySkuFilter';
const gallerySkuService = {
  /**
   * @param filter when set, skuCount only counts the SKUs it selects (see the
   * {@code ?skuIds=} / {@code ?skuTags=} company gallery filter)
   */
  getGalleryLabels: (filter?: GallerySkuFilter) => {
    const query = filter ? gallerySkuFilterQuery(filter) : '';
    return api.get<PlatformSkuLabel>(
      `/skus/gallery-labels${query ? `?${query}` : ''}`
    );
  },

  getSsoBuyerGalleryLabels: () => {
    return api.get<SsoBuyerGalleryLabel[]>(
      '/skus/sso-buyer-gallery-labels/list'
    );
  },

  createGalleryLabel: (payload: SkuGalleryLabelCreateParams) => {
    return api.post<SkuGalleryLabel>('/skus/gallery-labels', payload);
  },

  createCompanyLabel: (payload: SkuGalleryLabelCreateParams) => {
    return api.post<SkuGalleryLabel>('/skus/company-labels', payload);
  },

  updateGalleryLabel: (payload: SkuGalleryLabelUpdateParams) => {
    return api.put<SkuGalleryLabel>(
      `/skus/gallery-labels/${payload.id}`,
      payload
    );
  },

  updateCompanyLabel: (payload: SkuCompanyLabelUpdateParams) => {
    return api.put<SkuGalleryLabel>(
      `/skus/company-labels/${payload.id}`,
      payload
    );
  },

  getPreConfiguredSkus: (payload: BUSINESS_TYPE_ENUM | undefined) => {
    return api.get<PreConfiguredSkuGallery[]>(
      `/skus/gallery-labels/pre-configured-skus${
        payload ? '?businessType=' + payload : ''
      }`
    );
  },

  removeGalleryLabel: (payload: number) => {
    return api.delete<void>(`/skus/gallery-labels/${payload}`, payload);
  },

  removeCompanyLabel: (payload: number) => {
    return api.delete<void>(`/skus/company-labels/${payload}`, payload);
  },

  getGallerySkusByBusinessType: (businessType: BUSINESS_TYPE_ENUM) => {
    return api.get<Paged<GallerySku>>(
      `/skus/gallery?page=0&size=1000&businessType=${businessType}`
    );
  },

  getGallerySkusByLabel: (galleryLabelId: number) => {
    return api.get<Paged<GallerySku>>(
      `/skus/gallery?page=0&size=1000&galleryLabelId=${galleryLabelId}`
    );
  },

  getSsoBuyerGallerySkusByLabel: (galleryLabelId: number) => {
    return api.get<GallerySku[]>(`/skus/sso/buyer/gallery/${galleryLabelId}`);
  },

  getCompanySkusByLabel: ({
    companyLabelId,
    galleryLabelId,
    page,
    size,
    filter,
    designSize,
    matchedOnly,
  }: {
    companyLabelId?: number;
    /** Product category, as opposed to the company category companyLabelId selects. */
    galleryLabelId?: number;
    page?: number;
    size?: number;
    filter?: GallerySkuFilter;
    designSize?: GalleryDesignSize;
    matchedOnly?: boolean;
  }) => {
    // Separate concerns joined into one query: the company label picks a category, the filter
    // narrows which SKUs come back, the design size orders them, and matchedOnly keeps only the
    // ones that can hold the design. See utils/galleryDesignSize for why size stays out of filter.
    //
    // companyLabelId is omitted rather than sent as -1 for the match view: -1 means "any
    // category" to the API, which excludes every SKU that has no company label at all -- and on
    // the Canva org that is all of them.
    const query = [
      companyLabelId != null ? `companyLabelId=${companyLabelId}` : '',
      // Widened to the category's sub-categories: a top-level product category can hold no SKU
      // of its own and carry all of them below it ("Booklets" -> "Saddle Stitch", "Coil", ...),
      // while the label list counts those towards the parent. Asking for the parent alone comes
      // back empty for exactly the categories the gallery showed as non-empty.
      galleryLabelId != null
        ? `galleryLabelId=${galleryLabelId}&includeChildLabels=true`
        : '',
      filter ? gallerySkuFilterQuery(filter) : '',
      galleryDesignSizeQuery(designSize),
      matchedOnly ? 'matchedOnly=true' : '',
    ]
      .filter(Boolean)
      .join('&');
    return api.get<Paged<GallerySku>>(
      `/skus/gallery?page=${Number(page) - 1 || 0}&size=${size || 12}${
        query ? `&${query}` : ''
      }`
    );
  },
};
export default gallerySkuService;
